"use client";

/**
 * ContentSkeleton — the AuthGuard content-area fallback.
 *
 * The gate now renders each SCREEN optimistically while it revalidates (see
 * AuthGuard's gateState==='loading' branch), so every screen shows its OWN
 * loading (home's dashboard skeleton, the new-task composer, route loading.tsx).
 * This fallback therefore only covers the pre-auth window (before `user`
 * resolves) and the onboarding redirect tick.
 *
 * It starts as a CLEAN layout spacer — no content-shaped rectangles. A generic
 * skeleton can never match the destination screen, so it reads as misplaced
 * content; a quiet held space, with the sidebar + header already painted, is
 * calmer and never wrong — for the FAST path (~300ms P99).
 *
 * But the window is only "brief" on a good connection. On a slow link the
 * auth refresh stretches it to seconds, and pure white beside a live sidebar
 * reads as broken (founder, live 2026-08-02). Past SLOW_REVEAL_MS the held
 * space must say "alive": one breathing dot — quieter than any skeleton,
 * never content-shaped, and never the brand mark (that stays reserved for
 * true full-page boots so it can't read as "the loader comes back every time").
 */

import { useEffect, useState } from "react";
import { traceFlash } from "@/lib/flash-trace";

const SLOW_REVEAL_MS = 600;

export function ContentSkeleton() {
  const [slow, setSlow] = useState(false);
  // Mid-session, this held space replacing the content IS a visible flash:
  // the flash recorder hears it through this trace (flash-report.ts).
  useEffect(() => {
    traceFlash("loading", "content-skeleton");
  }, []);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), SLOW_REVEAL_MS);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="flex-1 min-h-50 flex items-center justify-center">
      {slow && (
        <span
          className="w-2.5 h-2.5 rounded-full bg-content-skeleton animate-content-pulse motion-reduce:animate-none motion-reduce:opacity-50"
          role="status"
          aria-label="Loading"
        />
      )}
    </div>
  );
}
